import type { AuditEventResponse } from '@malaberg/shared';
/**
 * AuditEventRow — T29.
 *
 * Renders a single row in the audit log table. Clicking expands the
 * payload JSON inline via Mantine Collapse.
 *
 * Behavior:
 * - Acting user: shows the actingUserId value; if imp_admin_id is set in
 *   payload, appends a gray "via impersonation by <admin name>" badge.
 * - Event type: code-styled chip showing the snake_case event type.
 * - Entity: "<entityType>#<entityId>", or "—" when entityType is null.
 * - Payload: clicking the row expands a <pre> block with pretty-printed JSON.
 */
import { Collapse, Text } from '@mantine/core';
import { useState } from 'react';

interface Props {
  event: AuditEventResponse;
}

export function AuditEventRow({ event }: Props) {
  const [expanded, setExpanded] = useState(false);

  const impAdminId =
    typeof event.payload.imp_admin_id === 'string' ? event.payload.imp_admin_id : null;
  const impAdminName =
    typeof event.payload.imp_admin_name === 'string' ? event.payload.imp_admin_name : null;

  const entityText = event.entityType != null ? `${event.entityType}#${event.entityId ?? ''}` : '—';

  const formattedDate = new Date(event.createdAt).toLocaleString();

  return (
    <>
      <tr
        data-testid="audit-event-row"
        onClick={() => setExpanded((v) => !v)}
        style={{ cursor: 'pointer' }}
      >
        {/* When */}
        <td data-testid="audit-event-when">{formattedDate}</td>

        {/* Acting user */}
        <td data-testid="audit-event-acting-user">
          <span>{event.actingUserId ?? '—'}</span>
          {impAdminId != null && (
            <Text component="span" size="xs" c="dimmed" ml={6} data-testid="impersonation-badge">
              via impersonation by {impAdminName ?? impAdminId}
            </Text>
          )}
        </td>

        {/* Event type — code-styled chip */}
        <td>
          <code data-testid="event-type-chip">{event.eventType}</code>
        </td>

        {/* Entity */}
        <td data-testid="audit-entity">{entityText}</td>

        {/* Payload column header cell — clicking the whole row toggles */}
        <td>
          <Text size="xs" c="dimmed">
            {expanded ? '▲ hide' : '▼ show'}
          </Text>
        </td>
      </tr>

      {/* Payload expansion row */}
      {expanded && (
        <tr data-testid="audit-payload-row">
          <td colSpan={5} style={{ padding: 0 }}>
            <Collapse in={expanded}>
              <pre
                data-testid="audit-payload-json"
                style={{
                  margin: 0,
                  padding: '8px 16px',
                  fontSize: '12px',
                  background: 'var(--mantine-color-gray-0, #f8f9fa)',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {JSON.stringify(event.payload, null, 2)}
              </pre>
            </Collapse>
          </td>
        </tr>
      )}
    </>
  );
}
